/**
 * Live API client for Lalita Soujanya.
 * Communicates with FastAPI backend, manages auth token in headers & cookies,
 * formats query params and handles image URLs.
 */

const browserOrigin =
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "";

// Frontend and API are served through the same Nginx origin in production.
// Using the runtime origin prevents a local development .env value from ever
// being baked into a production bundle and sent to a visitor's own computer.
const BACKEND_URL = (
  process.env.NODE_ENV === "production"
    ? browserOrigin
    : process.env.REACT_APP_BACKEND_URL || browserOrigin
).replace(/\/+$/, "");

const TOKEN_KEY = "ls_auth_token_v1";

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
}

export function clearToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {}
}

async function request(method, path, body, config = {}) {
  let url = path.startsWith("http")
    ? path
    : `${BACKEND_URL}${path.startsWith("/") ? "" : "/"}${path}`;

  // Process query params
  if (config.params) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(config.params)) {
      if (v !== undefined && v !== null && v !== "") {
        params.append(k, String(v));
      }
    }
    const qs = params.toString();
    if (qs) {
      url += (url.includes("?") ? "&" : "?") + qs;
    }
  }

  const headers = { ...(config.headers || {}) };
  const token = getToken();
  if (token && !headers["Authorization"]) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;
  // The browser must generate FormData's multipart boundary. A manually set
  // multipart Content-Type omits that boundary and FastAPI rejects the upload.
  if (isFormData) {
    Object.keys(headers).forEach((key) => {
      if (key.toLowerCase() === "content-type") delete headers[key];
    });
  }
  if (!isFormData && body !== null && body !== undefined && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  const fetchOptions = {
    method,
    headers,
    credentials: "include",
  };

  if (config.signal) fetchOptions.signal = config.signal;
  if (config.cache) fetchOptions.cache = config.cache;

  if (body !== null && body !== undefined) {
    fetchOptions.body = isFormData ? body : JSON.stringify(body);
  }

  let res;
  try {
    res = await fetch(url, fetchOptions);
  } catch (netErr) {
    const error = new Error(netErr.message || "Network error. Backend server may be offline.");
    error.isNetworkError = true;
    throw error;
  }

  // Parse response
  let data;
  const contentType = res.headers.get("content-type") || "";
  if (config.responseType === "blob" && res.ok) {
    data = await res.blob();
  } else if (contentType.includes("application/json")) {
    try {
      data = await res.json();
    } catch {
      data = null;
    }
  } else {
    data = await res.text();
  }

  if (!res.ok) {
    const error = new Error(
      typeof data === "object" && data?.detail
        ? typeof data.detail === "string"
          ? data.detail
          : JSON.stringify(data.detail)
        : res.statusText || `Request failed with status ${res.status}`
    );
    error.response = { status: res.status, data, headers: res.headers };
    throw error;
  }

  // Intercept token on login/register/logout
  if (data && typeof data === "object" && data.token) {
    setToken(data.token);
  }
  if (path.includes("/auth/logout")) {
    clearToken();
  }

  return { data, status: res.status, headers: res.headers };
}

const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

async function getWithRetry(url, config = {}) {
  const delays = [0, 1200, 2500, 5000, 9000, 14000, 18000];
  let lastError;
  for (let attempt = 0; attempt < delays.length; attempt += 1) {
    if (delays[attempt]) await wait(delays[attempt]);
    try {
      return await request("GET", url, null, config);
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      const retryable = error?.isNetworkError || !status || status === 408 || status === 429 || status >= 500;
      if (!retryable) throw error;
    }
  }
  throw lastError;
}

// ============================================================
// Public axios-like API
// ============================================================
export const api = {
  // Render free instances can sleep between visits. Retry transient reads so
  // a cold start remains a loading state instead of looking like an empty shop.
  get: (url, config) => getWithRetry(url, config),
  getOnce: (url, config) => request("GET", url, null, config),
  post: (url, body, config) => request("POST", url, body, config),
  put: (url, body, config) => request("PUT", url, body, config),
  patch: (url, body, config) => request("PATCH", url, body, config),
  delete: (url, config) => request("DELETE", url, null, config),
  download: (url, config = {}) => request("GET", url, null, { ...config, responseType: "blob" }),
};

const FALLBACK_PHOTO = "/assets/couture/women_bridal.jpg";

export const imgUrl = (path) => {
  if (!path) return FALLBACK_PHOTO;
  if (path.startsWith("data:") || path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  if (path.startsWith("/api/uploads/")) {
    return `${BACKEND_URL}${path}`;
  }
  return `${process.env.PUBLIC_URL || ""}${path.startsWith("/") ? "" : "/"}${path}`;
};

export const money = (n) =>
  n == null ? "Price on request" : `₹${Number(n).toLocaleString("en-IN")}`;

const validationMessage = (item) => {
  if (!item || typeof item !== "object") return String(item || "Invalid value");
  const path = Array.isArray(item.loc)
    ? item.loc.filter((part) => part !== "body").join(" → ")
    : "";
  const message = typeof item.msg === "string" ? item.msg : JSON.stringify(item);
  return path ? `${path}: ${message}` : message;
};

export const formatApiError = (err) => {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (Array.isArray(detail) && detail.length) return detail.map(validationMessage).join(" ");
  if (detail && typeof detail === "object") return JSON.stringify(detail);

  if (err?.isNetworkError) {
    return "The server could not be reached. Check the internet connection and confirm that the website service is running, then try again.";
  }

  const responseText = err?.response?.data;
  if (
    typeof responseText === "string" &&
    responseText.trim() &&
    responseText.trim().toLowerCase() !== "internal server error"
  ) {
    return responseText.trim();
  }

  if (status >= 500) {
    return `The server could not complete this request (HTTP ${status}). Your existing data has not been intentionally changed. Please try once more and check the server logs if it continues.`;
  }
  if (status) {
    return `The request was rejected (HTTP ${status}${err?.message ? `: ${err.message}` : ""}).`;
  }
  return err?.message || "Something went wrong. Please try again.";
};
